"use client";

import { c as _c } from "react/compiler-runtime";
import { CarePlanLogAction, CarePlanLogField, CarePlanLogSource } from "../types";
import { RiAddCircleLine, RiDeleteBinLine, RiEditLine, RiExchangeLine } from "react-icons/ri";
import { formatDate, formatList, formatStaffName, formatToHumanReadable } from "@/lib/format-data";
import { cx } from "@/lib/tailwind-utils";
import { type LogGroup } from "./use-grouped-logs";
import { type CarePlanLogEntry } from "@/api/provider/patient/care-plan";
interface AuditTrailTimelineProps {
  groups: LogGroup[];
  selectedIndex: number;
  onSelect: (index: number) => void;
  patientName: string | null;
}
export function AuditTrailTimeline(t0) {
  const $ = _c(13);
  const {
    groups,
    selectedIndex,
    onSelect,
    patientName
  } = t0;
  let t1;
  if ($[0] !== patientName) {
    t1 = entry => {
      if (entry.source === CarePlanLogSource.PATIENT_SELF_SCHEDULE) {
        return patientName;
      }
      return entry.createdById ? formatStaffName(entry.createdById) : null;
    };
    $[0] = patientName;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const getActorName = t1;
  let t2;
  if ($[2] !== getActorName || $[3] !== groups || $[4] !== onSelect || $[5] !== selectedIndex) {
    let t3;
    if ($[7] !== getActorName || $[8] !== onSelect || $[9] !== selectedIndex) {
      t3 = (group, idx) => {
        const entries = group.type === "standalone" ? [group.entry] : group.entries;
        const referenceEntry = entries[0];
        const isSelected = idx === selectedIndex;
        const actorName = getActorName(referenceEntry);
        return <button key={group.type === "batch" ? group.batchId : referenceEntry.id} type="button" className={cx("flex items-start gap-3 rounded-md px-3 py-2.5 text-left transition-colors", "w-full", isSelected ? "bg-decoda-blue/10 ring-1 ring-decoda-blue/30" : "hover:bg-hover")} onClick={() => onSelect(idx)}><div className="flex flex-col items-center pt-0.5"><ActionIcon action={referenceEntry.action} isSelected={isSelected} /></div><div className="min-w-0 flex-1"><p className={cx("truncate text-body-strong", isSelected ? "text-foreground" : "text-foreground-secondary")}>{getGroupSummary(group)}</p><div className="mt-0.5 flex items-center gap-2 text-caption text-foreground-muted">{actorName && <span>{actorName}</span>}<span>{formatDate(referenceEntry.createdDate)}</span></div>{group.type === "batch" && <span className="mt-0.5 inline-block text-tiny text-foreground-muted">{group.entries.length} changes</span>}</div></button>;
      };
      $[7] = getActorName;
      $[8] = onSelect;
      $[9] = selectedIndex;
      $[10] = t3;
    } else {
      t3 = $[10];
    }
    t2 = groups.map(t3);
    $[2] = getActorName;
    $[3] = groups;
    $[4] = onSelect;
    $[5] = selectedIndex;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[11] !== t2) {
    t3 = <>{t2}</>;
    $[11] = t2;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  return t3;
}

// ============================================================================
// Helpers
// ============================================================================

function ActionIcon(t0) {
  const $ = _c(10);
  const {
    action,
    isSelected
  } = t0;
  const t1 = isSelected ? "text-decoda-blue" : "text-foreground-muted";
  let t2;
  if ($[0] !== t1) {
    t2 = cx("size-4", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const iconClass = t2;
  switch (action) {
    case CarePlanLogAction.CREATED:
    case CarePlanLogAction.CYCLE_CREATED:
      {
        let t3;
        if ($[2] !== iconClass) {
          t3 = <RiAddCircleLine className={iconClass} />;
          $[2] = iconClass;
          $[3] = t3;
        } else {
          t3 = $[3];
        }
        return t3;
      }
    case CarePlanLogAction.REMOVED:
    case CarePlanLogAction.CYCLE_REMOVED:
      {
        let t3;
        if ($[4] !== iconClass) {
          t3 = <RiDeleteBinLine className={iconClass} />;
          $[4] = iconClass;
          $[5] = t3;
        } else {
          t3 = $[5];
        }
        return t3;
      }
    case CarePlanLogAction.STATUS_CHANGED:
      {
        let t3;
        if ($[6] !== iconClass) {
          t3 = <RiExchangeLine className={iconClass} />;
          $[6] = iconClass;
          $[7] = t3;
        } else {
          t3 = $[7];
        }
        return t3;
      }
    default:
      {
        let t3;
        if ($[8] !== iconClass) {
          t3 = <RiEditLine className={iconClass} />;
          $[8] = iconClass;
          $[9] = t3;
        } else {
          t3 = $[9];
        }
        return t3;
      }
  }
}
function getGroupSummary(group: LogGroup): string {
  if (group.type === "standalone") {
    return getSingleEntrySummary(group.entry);
  }
  return getBatchSummary(group.entries);
}
function getSingleEntrySummary(entry: CarePlanLogEntry): string {
  if (entry.action === CarePlanLogAction.CYCLE_CREATED) return "Cycle Created";
  if (entry.action === CarePlanLogAction.CYCLE_UPDATED) return "Cycle Updated";
  if (entry.action === CarePlanLogAction.CYCLE_REMOVED) return "Cycle Removed";
  const action = formatToHumanReadable(entry.action);
  if (entry.fieldName) {
    const field = entry.fieldName.replace(/_IDS?$/, "").replace(/^STEP_/, "");
    return `${action} — ${formatToHumanReadable(field)}`;
  }
  return action;
}
function getBatchSummary(entries: CarePlanLogEntry[]): string {
  // If a batch contains a cycle action, surface it as the primary label
  const cycleEntry = entries.find(e => e.action === CarePlanLogAction.CYCLE_CREATED || e.action === CarePlanLogAction.CYCLE_UPDATED || e.action === CarePlanLogAction.CYCLE_REMOVED);
  if (cycleEntry) return getSingleEntrySummary(cycleEntry);
  const uniqueActions = Array.from(new Set(entries.map(e => e.action)));
  const actions = formatList(uniqueActions.map(formatToHumanReadable));
  const stepType = entries.find(f => f.fieldName === CarePlanLogField.STEP_TYPE)?.newValue ?? entries.find(f => f.fieldName === CarePlanLogField.STEP_TYPE)?.oldValue;
  const description = entries.find(f => f.fieldName === CarePlanLogField.STEP_DESCRIPTION)?.newValue ?? entries.find(f => f.fieldName === CarePlanLogField.STEP_DESCRIPTION)?.oldValue;
  let label = actions;
  if (stepType) label += ` — ${formatToHumanReadable(stepType)}`;
  if (description) label += `: ${description}`;
  return label;
}